import { useState, useRef } from 'react';
import { useAuth } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ImageUp, Link2, Copy, CheckCircle2, ExternalLink, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const IMGTOOL_WEB = 'https://viubux.com/imagegallery/imgtool/';

const ImgToolPanel = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const fileRef = useRef(null);

  const [uploading, setUploading] = useState(false);
  const [srcUrl, setSrcUrl] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('image', file);
      const res = await axios.post(`${API}/imgtool/upload`, form, { headers });
      setResult(res.data);
      toast.success('Imagen alojada en ImgTool');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  const uploadUrl = async () => {
    if (!srcUrl.startsWith('http')) return toast.error('Pega una URL válida (https://...)');
    setUploading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/imgtool/upload-url`, { url: srcUrl }, { headers });
      setResult(res.data);
      toast.success('Imagen alojada en ImgTool');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo alojar la imagen');
    } finally {
      setUploading(false);
    }
  };

  const copy = async () => {
    if (!result?.url) return;
    await navigator.clipboard.writeText(result.url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="space-y-6" data-testid="imgtool-panel">
      <div>
        <h2 className="gotham-bold text-xl text-white flex items-center gap-2">
          <ImageUp className="w-5 h-5 text-emerald-400" /> Viubux ImgTool — Alojamiento de imágenes
        </h2>
        <p className="verdana-regular text-white/60 text-sm">
          Sube una imagen y obtén un enlace permanente en <strong>viubux.com</strong> para usarlo en avatares,
          banners, portadas o cualquier parte del sitio.
        </p>
      </div>

      {/* How to use */}
      <Card className="card-viubux-dark border-white/10" data-testid="imgtool-howto">
        <CardHeader className="pb-2">
          <CardTitle className="gotham-medium text-white text-base">¿Cómo se usa?</CardTitle>
        </CardHeader>
        <CardContent className="text-white/70 text-sm space-y-2">
          <p>1. Arrastra o elige una imagen (JPG, PNG, GIF o WEBP, hasta 8MB), o pega una URL de origen.</p>
          <p>2. La herramienta la aloja y te da un <strong>enlace permanente</strong>.</p>
          <p>3. Copia el enlace y pégalo donde lo necesites (portada de obra, banner, etc.).</p>
          <a
            href={IMGTOOL_WEB}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-emerald-300 hover:text-emerald-200 mt-1"
            data-testid="imgtool-open-web"
          >
            <ExternalLink className="w-4 h-4" /> Abrir ImgTool en el navegador (código de acceso del equipo)
          </a>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* File upload */}
        <Card className="card-viubux-dark border-emerald-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="gotham-medium text-white text-base flex items-center gap-2">
              <UploadCloud className="w-4 h-4 text-emerald-400" /> Subir un archivo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden"
              data-testid="imgtool-file-input"
              onChange={(e) => uploadFile(e.target.files?.[0])}
            />
            <Button
              data-testid="imgtool-choose-file-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="btn-viubux w-full"
            >
              <ImageUp className="w-4 h-4 mr-2" />
              {uploading ? 'Subiendo...' : 'Elegir imagen'}
            </Button>
          </CardContent>
        </Card>

        {/* URL rehost */}
        <Card className="card-viubux-dark border-cyan-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="gotham-medium text-white text-base flex items-center gap-2">
              <Link2 className="w-4 h-4 text-cyan-400" /> Alojar desde una URL
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <input
              type="url"
              value={srcUrl}
              onChange={(e) => setSrcUrl(e.target.value)}
              placeholder="https://..."
              data-testid="imgtool-url-input"
              className="w-full px-4 py-2.5 rounded-xl bg-black/40 border border-cyan-500/30 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
            />
            <Button
              data-testid="imgtool-upload-url-btn"
              onClick={uploadUrl}
              disabled={uploading || !srcUrl}
              variant="outline"
              className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 w-full"
            >
              <Link2 className="w-4 h-4 mr-2" />
              {uploading ? 'Alojando...' : 'Alojar imagen'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Result */}
      {result?.url && (
        <Card className="card-viubux-dark border-emerald-500/30" data-testid="imgtool-result">
          <CardContent className="p-4 flex flex-col sm:flex-row items-center gap-4">
            <img
              src={result.url}
              alt="preview"
              className="w-20 h-20 object-cover rounded-lg border border-white/10 bg-black/30"
              data-testid="imgtool-result-preview"
            />
            <div className="flex-1 w-full">
              <div className="text-white/60 text-xs mb-1">Enlace permanente</div>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={result.url}
                  data-testid="imgtool-result-url"
                  className="flex-1 px-3 py-2 rounded-lg bg-black/50 border border-white/10 text-white text-sm"
                />
                <Button
                  data-testid="imgtool-copy-btn"
                  onClick={copy}
                  className={copied ? 'bg-emerald-600 hover:bg-emerald-600' : 'btn-viubux'}
                >
                  {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ImgToolPanel;
